import { api } from "@/lib/api";
import type { PageDto, PageResponse, PageVersionDto } from "@/types/api";

export interface PageParams {
    page?: number;
    size?: number;
    sort?: string; // e.g. "createdAt,desc"
    q?: string;    // case-insensitive name filter
    /** Advertiser filter. -1 means "IS NULL" (unassigned). */
    advertiserId?: number;
}
function paramsOf(p: PageParams = {}): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    if (p.page != null) out.page = p.page;
    if (p.size != null) out.size = p.size;
    if (p.sort) out.sort = p.sort;
    if (p.q && p.q.trim()) out.q = p.q.trim();
    if (p.advertiserId != null) out.advertiserId = p.advertiserId;
    return out;
}

export interface CreatePageRequest {
    name: string;
    description?: string | null;
}

export async function listPagesPaged(params: PageParams = {}): Promise<PageResponse<PageDto>> {
    const { data } = await api.get<PageResponse<PageDto>>("/admin/pages", { params: paramsOf(params) });
    return data;
}

export async function listPages(): Promise<PageDto[]> {
    // Backward-compat convenience: fetches first large page and returns the content array.
    const data = await listPagesPaged({ size: 1000 });
    return data.content;
}

export async function getPage(id: number): Promise<PageDto> {
    const { data } = await api.get<PageDto>(`/admin/pages/${id}`);
    return data;
}

export async function createPage(request: CreatePageRequest): Promise<PageDto> {
    const { data } = await api.post<PageDto>("/admin/pages", request);
    return data;
}

export async function updatePage(id: number, request: Partial<CreatePageRequest>): Promise<PageDto> {
    const { data } = await api.patch<PageDto>(`/admin/pages/${id}`, request);
    return data;
}

export async function deletePage(id: number): Promise<void> {
    await api.delete(`/admin/pages/${id}`);
}

export async function listVersionsPaged(pageId: number, params: PageParams = {}): Promise<PageResponse<PageVersionDto>> {
    const { data } = await api.get<PageResponse<PageVersionDto>>(
        `/admin/pages/${pageId}/versions`,
        { params: paramsOf(params) },
    );
    return data;
}

export async function listVersions(pageId: number): Promise<PageVersionDto[]> {
    const data = await listVersionsPaged(pageId, { size: 1000 });
    return data.content;
}

/**
 * Domains currently serving any version of the page. Single-roundtrip
 * replacement for the previous "load every domain client-side and filter"
 * pattern. Server query is one SQL with an IN-clause.
 */
export async function listServingDomains(pageId: number): Promise<import("@/types/api").DomainDto[]> {
    const { data } = await api.get<import("@/types/api").DomainDto[]>(`/admin/pages/${pageId}/serving-domains`);
    return data;
}

export async function uploadVersion(pageId: number, bundle: File): Promise<PageVersionDto> {
    const form = new FormData();
    form.append("bundle", bundle);
    const { data } = await api.post<PageVersionDto>(`/admin/pages/${pageId}/versions`, form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
}

/**
 * URL of a version's bundle zip. Same authenticated same-origin pattern as
 * the preview URL — usable directly as an <a href>; the browser downloads
 * with the server-provided filename (page-{id}-v{num}.zip).
 */
export function downloadVersionUrl(pageId: number, versionId: number): string {
    return `/admin/pages/${pageId}/versions/${versionId}/download`;
}

/** VALIDATED → RETIRED. Backend 409s when the version is live on a domain. */
export async function retireVersion(pageId: number, versionId: number): Promise<PageVersionDto> {
    const { data } = await api.post<PageVersionDto>(
        `/admin/pages/${pageId}/versions/${versionId}/retire`,
    );
    return data;
}

/** Hard-delete a version + its bundle. Backend 409s when live on a domain or referenced by leads/visits. */
export async function deleteVersion(pageId: number, versionId: number): Promise<void> {
    await api.delete(`/admin/pages/${pageId}/versions/${versionId}`);
}

export async function updateManifest(
    pageId: number,
    versionId: number,
    manifest: unknown,
): Promise<PageVersionDto> {
    const { data } = await api.patch<PageVersionDto>(
        `/admin/pages/${pageId}/versions/${versionId}/manifest`,
        manifest,
    );
    return data;
}
